import { useMemo } from "react";
import { useSelector } from "react-redux";
import { resolveRoleKey, selectUser } from "../../auth";
import { PATHS } from "../../config/routePaths";

/** Keys used with `sidebarSection` on nav items; labels are for sidebar accordion headers. */
export const SIDEBAR_SECTION_LABELS = {
  browse: "Browse",
  topics: "Topics",
  library: "Library & activity",
  community: "Community",
  account: "Account",
  workspace: "Workspace",
  admin_core: "Core",
  admin_ops: "Operations & support",
  admin_platform: "Platform & content",
  admin_system: "System & tools",
};

const SIDEBAR_SECTION_ORDER_BY_ROLE = {
  reader: ["browse", "topics", "library", "community", "account"],
  writer: ["workspace"],
  superadmin: ["admin_core", "admin_ops", "admin_platform", "admin_system"],
};

const NAV_BY_ROLE = {
  public: [
    { label: "Read", href: PATHS.browse(), icon: "bi-book", showIn: ["publicHeader"], headerNavPrimary: true },
    { label: "Trending", href: PATHS.TRENDING, icon: "bi-fire", showIn: ["publicHeader"], headerNavPrimary: true },
    { label: "Writers", href: PATHS.WRITERS, icon: "bi-people", showIn: ["publicHeader"], headerNavPrimary: true },
    {
      label: "Write",
      href: `${PATHS.AUTH_LOGIN}?redirect=${encodeURIComponent(PATHS.BECOME_WRITER)}`,
      icon: "bi-pencil-square",
      showIn: ["publicHeader"],
      headerNavPrimary: true,
    },
    { label: "Login", href: PATHS.AUTH_LOGIN, icon: "bi-box-arrow-in-right", showIn: ["publicHeader"], headerNavPrimary: true },
  ],
  reader: [
    { id: "home", label: "Home", href: PATHS.APP_DASHBOARD, icon: "bi-house-door", showIn: ["sidebar"], sidebarSection: "browse" },
    { id: "explore", label: "Read", href: PATHS.browse(), icon: "bi-book", showIn: ["sidebar", "publicHeader"], headerNavPrimary: true, sidebarSection: "browse" },
    { label: "Trending", href: PATHS.TRENDING, icon: "bi-fire", showIn: ["publicHeader"], headerNavPrimary: true },
    { label: "Writers", href: PATHS.WRITERS, icon: "bi-people", showIn: ["publicHeader"], headerNavPrimary: true },
    { id: "library", label: "Library", href: PATHS.APP_LIBRARY, icon: "bi-bookmark-heart", showIn: ["sidebar", "publicHeader"], headerNavPrimary: true, sidebarSection: "browse" },
    { id: "following", label: "Following", href: PATHS.APP_FOLLOWING, icon: "bi-people", showIn: ["sidebar"], sidebarSection: "browse" },
    { id: "write", label: "Write", href: PATHS.BECOME_WRITER, icon: "bi-pencil", showIn: ["sidebar"], sidebarSection: "browse" },
    { id: "profile", label: "Profile", href: PATHS.APP_SETTINGS, icon: "bi-person", showIn: ["sidebar"], sidebarSection: "browse" },
  ],
  writer: [
    { id: "home", label: "Home", href: PATHS.APP_DASHBOARD, icon: "bi-house-door", showIn: ["sidebar"], sidebarSection: "workspace" },
    { id: "explore", label: "Read", href: PATHS.browse(), icon: "bi-book", showIn: ["sidebar", "publicHeader"], headerNavPrimary: true, sidebarSection: "workspace" },
    { label: "Trending", href: PATHS.TRENDING, icon: "bi-fire", showIn: ["publicHeader"], headerNavPrimary: true },
    { label: "Writers", href: PATHS.WRITERS, icon: "bi-people", showIn: ["publicHeader"], headerNavPrimary: true },
    { id: "library", label: "Library", href: PATHS.APP_LIBRARY, icon: "bi-bookmark-heart", showIn: ["sidebar", "publicHeader"], headerNavPrimary: true, sidebarSection: "workspace" },
    { id: "following", label: "Following", href: PATHS.APP_FOLLOWING, icon: "bi-people", showIn: ["sidebar"], sidebarSection: "workspace" },
    { id: "write", label: "Write", href: PATHS.contentNew(), icon: "bi-pencil", showIn: ["sidebar"], sidebarSection: "workspace" },
    { id: "works", label: "My Works", href: PATHS.APP_CONTENT, icon: "bi-file-text", showIn: ["sidebar"], sidebarSection: "workspace" },
    { id: "profile", label: "Profile", href: PATHS.APP_SETTINGS, icon: "bi-person", showIn: ["sidebar"], sidebarSection: "workspace" },
  ],
  superadmin: [
    { label: "Dashboard", href: PATHS.ADMIN_DASHBOARD, icon: "bi-speedometer2", showIn: ["sidebar"], headerNavPrimary: true, sidebarSection: "admin_core" },
    { label: "Users", href: PATHS.ADMIN_USERS, icon: "bi-people", showIn: ["sidebar"], headerNavPrimary: true, sidebarSection: "admin_core" },
    { label: "Content", href: PATHS.ADMIN_CONTENT, icon: "bi-file-text", showIn: ["sidebar"], headerNavPrimary: true, sidebarSection: "admin_core" },
    { label: "Moderation", href: PATHS.ADMIN_MODERATION, icon: "bi-shield-check", showIn: ["sidebar"], sidebarSection: "admin_ops" },
    { label: "Reports", href: PATHS.ADMIN_REPORTS, icon: "bi-graph-up", showIn: ["sidebar"], sidebarSection: "admin_ops" },
    { label: "Enquiries", href: PATHS.ADMIN_ENQUIRIES, icon: "bi-envelope", showIn: ["sidebar"], sidebarSection: "admin_ops" },
    { label: "Settings", href: PATHS.ADMIN_SETTINGS, icon: "bi-gear", showIn: ["sidebar"], sidebarSection: "admin_system" },
    { label: "Categories", href: PATHS.ADMIN_CATEGORIES_TAGS, icon: "bi-tags", showIn: ["sidebar"], sidebarSection: "admin_platform" },
    { label: "Abuse", href: PATHS.ADMIN_ABUSE, icon: "bi-shield-exclamation", showIn: ["sidebar"], sidebarSection: "admin_ops" },
    { label: "Notifications", href: PATHS.ADMIN_NOTIFICATIONS_MGMT, icon: "bi-bell", showIn: ["sidebar"], sidebarSection: "admin_ops" },
    { label: "CMS", href: PATHS.ADMIN_CMS, icon: "bi-layout-text-window", showIn: ["sidebar"], sidebarSection: "admin_platform" },
    { label: "Feature flags", href: PATHS.ADMIN_FEATURE_FLAGS, icon: "bi-toggles", showIn: ["sidebar"], sidebarSection: "admin_platform" },
    { label: "SEO", href: PATHS.ADMIN_SEO, icon: "bi-search", showIn: ["sidebar"], sidebarSection: "admin_platform" },
    { label: "Backup", href: PATHS.ADMIN_BACKUP, icon: "bi-cloud-download", showIn: ["sidebar"], sidebarSection: "admin_system" },
    { label: "Feedback", href: PATHS.ADMIN_FEEDBACK, icon: "bi-star", showIn: ["sidebar"], sidebarSection: "admin_ops" },
    { label: "Roles", href: PATHS.ADMIN_ROLES, icon: "bi-person-badge", showIn: ["sidebar"], sidebarSection: "admin_system" },
    { label: "Bulk generator", href: PATHS.ADMIN_BULK_GENERATOR, icon: "bi-lightning", showIn: ["sidebar"], sidebarSection: "admin_system" },
  ],
};

const withPersonalLinks = (routes, user) =>
  routes.map((item) => {
    if (item.id === "profile" && user?.uid) {
      return { ...item, href: PATHS.PROFILE(user.uid) };
    }
    return item;
  });

export const useRoutes = () => {
  const user = useSelector(selectUser);
  return useMemo(() => {
    const roleKey = resolveRoleKey(user);
    return withPersonalLinks(NAV_BY_ROLE[roleKey] || [], user);
  }, [user]);
};

/**
 * Sidebar items grouped into ordered sections (for accordion UI).
 */
export const useSidebarSections = () => {
  const user = useSelector(selectUser);
  return useMemo(() => {
    const roleKey = resolveRoleKey(user);
    const routes = withPersonalLinks(NAV_BY_ROLE[roleKey] || [], user);
    const items = routes.filter((item) => item.showIn?.includes("sidebar"));
    const order = SIDEBAR_SECTION_ORDER_BY_ROLE[roleKey] || [];
    const grouped = new Map();
    for (const item of items) {
      const sid = item.sidebarSection || "default";
      if (!grouped.has(sid)) grouped.set(sid, []);
      grouped.get(sid).push(item);
    }
    const seen = new Set();
    const sections = [];
    for (const sid of order) {
      if (!grouped.has(sid)) continue;
      sections.push({
        id: sid,
        label: SIDEBAR_SECTION_LABELS[sid] || sid,
        items: grouped.get(sid),
      });
      seen.add(sid);
    }
    for (const [sid, sectionItems] of grouped) {
      if (seen.has(sid)) continue;
      sections.push({
        id: sid,
        label: SIDEBAR_SECTION_LABELS[sid] || sid,
        items: sectionItems,
      });
    }
    return sections;
  }, [user]);
};

export const useHomePath = () => {
  const routes = useRoutes();
  return routes?.[0]?.href || "/";
};
